import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from '../components/Helmet';
import { motion } from 'framer-motion';
import { CheckCircle, Trophy, User, Mail, Briefcase, Phone, Users, ChevronDown, Cpu, GraduationCap, ChevronRight } from 'lucide-react';
import { resolveLandingPage } from '../data/landingPages';
import { getCourseBySlug, getRecommendedCourses } from '../data/courses';
import { ExitIntentPopup } from '../components/ExitIntentPopup';

const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const lpData = resolveLandingPage(searchParams.get('id'), searchParams.get('ville'));

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    participants: '10 à 15',
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    utm_term: searchParams.get('utm_term') || '',
    utm_content: searchParams.get('utm_content') || '',
    gclid: searchParams.get('gclid') || '',
  });

  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [formStarted, setFormStarted] = useState(false);

  // Exit intent popup state
  const [showExitPopup, setShowExitPopup] = useState(false);
  const exitPopupShownRef = useRef(false);
  const pageLoadTimeRef = useRef(Date.now());
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrolledPercentageRef = useRef(0);

  const trackEvent = useCallback((eventName: string, eventData?: Record<string, unknown>) => {
    interface WindowWithGtag extends Window {
      gtag?: (action: string, event: string, data?: Record<string, unknown>) => void;
    }
    if (typeof window !== 'undefined' && (window as WindowWithGtag).gtag) {
      (window as WindowWithGtag).gtag?.('event', eventName, eventData);
    }
  }, []);

  const handleExitPopupClose = useCallback(() => {
    setShowExitPopup(false);
    trackEvent('exit_intent_dismiss');
  }, [trackEvent]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (!formStarted) {
      setFormStarted(true);
      trackEvent('form_start', { variant_id: lpData.id });
    }
  };

  // Exit intent popup logic
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    const canShowPopup = () => {
      const timeSinceLoad = Date.now() - pageLoadTimeRef.current;
      return (
        !submitted &&
        !exitPopupShownRef.current &&
        timeSinceLoad >= 1000
      );
    };

    if (isMobile) {
      // Mobile: track inactivity and scroll
      const handleMobileActivity = () => {
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
          if (canShowPopup()) {
            exitPopupShownRef.current = true;
            setShowExitPopup(true);
            trackEvent('exit_intent_popup_view');
          }
        }, 45000);
      };

      const handleScroll = () => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY;
        const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
        scrolledPercentageRef.current = scrollPercentage;

        if (scrollPercentage > 70 && canShowPopup()) {
          if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
          }
          exitPopupShownRef.current = true;
          setShowExitPopup(true);
          trackEvent('exit_intent_popup_view');
        }
      };

      window.addEventListener('scroll', handleScroll);
      window.addEventListener('mousemove', handleMobileActivity);
      window.addEventListener('touchstart', handleMobileActivity);

      handleMobileActivity();

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('mousemove', handleMobileActivity);
        window.removeEventListener('touchstart', handleMobileActivity);
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
      };
    } else {
      // Desktop: track mouseleave at top of page
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 0 && canShowPopup()) {
          exitPopupShownRef.current = true;
          setShowExitPopup(true);
          trackEvent('exit_intent_popup_view');
        }
      };

      document.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [submitted, trackEvent]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);

    const data = new FormData();
    data.append('access_key', '5bf31f55-ad2e-42d0-b75d-da315233314c');
    data.append('subject', `[Devis LP] ${formData.name} — ${formData.company}`);
    data.append('name', formData.name);
    data.append('email', formData.email);
    data.append('entreprise', formData.company);
    data.append('phone', formData.phone);
    data.append('participants', formData.participants);
    data.append('formation', lpData.preselectedFormationSlug);
    data.append('lp_source', lpData.lpSource);
    data.append('utm_source', formData.utm_source);
    data.append('utm_medium', formData.utm_medium);
    data.append('utm_campaign', formData.utm_campaign);
    data.append('utm_term', formData.utm_term);
    data.append('utm_content', formData.utm_content);
    data.append('gclid', formData.gclid);
    data.append('message',
      `Demande de devis depuis landing page\n\n` +
      `Nom : ${formData.name}\nEmail : ${formData.email}\nEntreprise : ${formData.company}\nTéléphone : ${formData.phone}\n` +
      `Participants : ${formData.participants}\nFormation : ${lpData.preselectedFormationSlug}\nSource LP : ${lpData.lpSource}`
    );

    try {
      const response = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: data });
      const res = await response.json();
      if (res.success) {
        setSubmitted(true);
        interface WindowWithGtag extends Window {
          gtag?: (action: string, event: string, data: Record<string, unknown>) => void;
        }
        const preselectedCourse = getCourseBySlug(lpData.preselectedFormationSlug);
        if (typeof window !== 'undefined' && (window as WindowWithGtag).gtag) {
          (window as WindowWithGtag).gtag?.('event', 'generate_lead', {
            variant_id: lpData.id,
            ville: lpData.ville?.nom ?? 'national',
            formation: preselectedCourse?.title,
            participants: formData.participants,
          });
        }
      }
    } catch {
      setSubmitted(true);
    } finally {
      setSending(false);
    }
  };

  const preselectedCourse = getCourseBySlug(lpData.preselectedFormationSlug);

  // Formations recommandées — maille interne vers les pages formation depuis les LP (noindex, follow).
  const recommendedCourses = [
    ...(preselectedCourse ? [preselectedCourse] : []),
    ...getRecommendedCourses(lpData.id, [...lpData.targetKeywords, lpData.h1, lpData.courseName], 3)
      .filter(c => c.id !== preselectedCourse?.id),
  ].slice(0, 2);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": lpData.faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": lpData.courseName,
    "description": lpData.subhead,
    "provider": {
      "@type": "Organization",
      "name": "Upnov IA",
      "sameAs": "https://www.upnov.com"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "onsite",
      "location": { "@type": "Country", "name": "France" }
    }
  };

  const formContent = submitted ? (
    <div className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-xl border border-gray-100 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full mb-4 sm:mb-5 md:mb-4">
        <CheckCircle className="h-6 sm:h-8 w-6 sm:w-8 text-green-600" />
      </div>
      <h2 className="text-lg sm:text-lg md:text-xl font-bold text-gray-900 mb-2 sm:mb-2">Merci, on vous contacte très vite !</h2>
      <p className="text-sm sm:text-sm md:text-base text-gray-600 mb-5 sm:mb-6 md:mb-6">Notre équipe reviendra vers vous sous 24h pour discuter de votre projet de formation.</p>
      <div className="flex flex-col gap-3">
        <a href="https://www.upnov.com/formation-ia-entreprise" className="w-full bg-primary text-white font-bold py-3 px-6 rounded-lg hover:bg-primary/90 transition-all text-center">
          Voir nos formations
        </a>
        <a href="https://www.upnov.com/accompagnement-ia-entreprise" className="w-full border-2 border-primary text-primary font-bold py-3 px-6 rounded-lg hover:bg-primary/5 transition-all text-center">
          Voir nos services IA
        </a>
      </div>
    </div>
  ) : (
    <div className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-xl border border-gray-100">
      <h2 className="text-lg sm:text-lg md:text-lg font-bold mb-4 sm:mb-4 md:mb-4 text-gray-900">Recevoir le programme détaillé</h2>

      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-3 md:space-y-3">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Nom complet *</label>
          <div className="relative">
            <User className="absolute left-3 top-2 h-4 sm:h-4 w-4 sm:w-4 text-gray-400" />
            <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full pl-10 pr-4 py-2 sm:py-2 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none" placeholder="Jean Dupont" />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Email professionnel *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full pl-10 pr-4 py-2 sm:py-2 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none" placeholder="vous@entreprise.com" />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Entreprise *</label>
          <div className="relative">
            <Briefcase className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input type="text" name="company" required value={formData.company} onChange={handleChange} className="w-full pl-10 pr-4 py-2 sm:py-2 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none" placeholder="Nom de votre entreprise" />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Téléphone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full pl-10 pr-4 py-2 sm:py-2 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none" placeholder="06 12 34 56 78" />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Nombre de participants</label>
          <div className="relative">
            <Users className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <select name="participants" value={formData.participants} onChange={handleChange} className="w-full pl-10 pr-4 py-2 sm:py-2 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none bg-white">
              <option value="Moins de 10">Moins de 10</option>
              <option value="10 à 20">10 à 20</option>
              <option value="20 à 50">20 à 50</option>
              <option value="Plus de 50">Plus de 50</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
          </div>
        </div>

        <input type="hidden" name="formation" value={lpData.preselectedFormationSlug} />
        <input type="hidden" name="lp_source" value={lpData.lpSource} />
        <input type="hidden" name="utm_source" value={formData.utm_source} />
        <input type="hidden" name="utm_medium" value={formData.utm_medium} />
        <input type="hidden" name="utm_campaign" value={formData.utm_campaign} />
        <input type="hidden" name="utm_term" value={formData.utm_term} />
        <input type="hidden" name="utm_content" value={formData.utm_content} />
        <input type="hidden" name="gclid" value={formData.gclid} />

        <button type="submit" disabled={sending} className="w-full bg-primary text-white font-bold py-2 sm:py-2 md:py-2 px-4 rounded-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 duration-200 text-sm disabled:opacity-70">
          {sending ? 'Envoi en cours...' : lpData.ctaLabel}
        </button>
        <p className="text-xs text-gray-500 text-center mt-3 sm:mt-4">
          Les informations transmises via ce formulaire sont utilisées par Upnov IA pour répondre à votre demande et assurer le suivi correspondant. Pour en savoir plus sur le traitement de vos données et vos droits, consultez notre{" "}
          <Link to="/politique-de-confidentialite/" className="underline hover:text-gray-700">
            Politique de confidentialité
          </Link>.
        </p>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Helmet>
        <title>{lpData.metaTitle}</title>
        <meta name="description" content={lpData.metaDescription} />
        <meta name="keywords" content={lpData.targetKeywords.join(', ')} />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={lpData.canonical} />

        <meta property="og:title" content={lpData.metaTitle} />
        <meta property="og:description" content={lpData.metaDescription} />
        <meta property="og:url" content={lpData.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Upnov IA" />

        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Minimal Header - Logo only */}
      <header className="bg-white py-3 sm:py-3 md:py-3 shadow-sm sticky top-0 z-50">
        <div className="container-custom">
          <div className="text-primary font-heading text-sm sm:text-base md:text-base font-bold inline-flex items-center gap-2">
            <Cpu className="h-5 sm:h-5 md:h-6 w-5 sm:w-5 md:w-6" />
            <span>UPNOV IA</span>
          </div>
        </div>
      </header>

      <main className="container-custom py-6 sm:py-8 md:py-8 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-8 lg:gap-12 items-start">
          {/* Left Column: Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6 sm:space-y-8 md:space-y-8 lg:space-y-10"
          >
            {/* Hero Section */}
            <section>
              <h1 className="text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4 leading-tight">
                {lpData.h1}
              </h1>
              <p className="text-base sm:text-lg md:text-lg lg:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed">
                {lpData.subhead}
              </p>

              {/* Social Proof Banner */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 md:p-5 mb-6 sm:mb-6 md:mb-6">
                {/* Stats - stacked on mobile, horizontal on desktop */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-4 mb-4 sm:mb-4 md:mb-4 pb-4 sm:pb-4 md:pb-4 border-b border-gray-100 md:border-b-0 md:pb-0 md:mb-0 md:grid-cols-2">
                  <div className="text-center">
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary mb-0.5">+1300</p>
                    <p className="text-xs sm:text-xs md:text-sm text-gray-600 font-medium">professionnels formés</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary mb-0.5">98%</p>
                    <p className="text-xs sm:text-xs md:text-sm text-gray-600 font-medium">de satisfaction</p>
                  </div>
                </div>

                {/* Client Logos */}
                <div className="pt-0 md:pt-4 md:border-t md:border-gray-100">
                  <p className="text-xs text-gray-500 font-medium mb-2 sm:mb-3 md:mb-3 uppercase tracking-wider">Ils nous font confiance</p>
                  <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 justify-center md:justify-start items-center">
                    <img src="/logo/logo-edf.webp" alt="EDF" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-ratp.webp" alt="RATP" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-loreal.webp" alt="L'Oréal" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-thales.webp" alt="Thales" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-vinci.webp" alt="Vinci" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-eiffage.webp" alt="Eiffage" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-sncf.webp" alt="SNCF" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-sagem.webp" alt="Sagem" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />
                    <img src="/logo/logo-carrefour.webp" alt="Carrefour" width="80" height="40" className="h-6 sm:h-6 md:h-7 w-auto grayscale hover:grayscale-0 transition-all duration-300" />

                  </div>
                </div>
              </div>
            </section>

            {/* OPCO Block */}
            <section className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-4 md:p-5">
              <div className="flex items-start gap-3 sm:gap-3 md:gap-3">
                <Trophy className="w-5 sm:w-5 md:w-6 h-5 sm:h-5 md:h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base sm:text-base md:text-lg font-bold text-blue-900 mb-1 sm:mb-1">{lpData.opcoTitre || "Finançable par votre OPCO"}</h3>
                  <p className="text-sm sm:text-sm md:text-sm text-blue-800">{lpData.opcoNote}</p>
                </div>
              </div>
            </section>

            {/* Benefits Section */}
            <section>
              <h2 className="text-2xl sm:text-2xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">Ce que vous allez apprendre</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-4">
                {lpData.benefits.map((benefit, idx) => (
                  <div
                    key={idx}
                    className="bg-white p-4 sm:p-5 md:p-4 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex items-start gap-3 sm:gap-3 md:gap-3">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-primary/10">
                          <CheckCircle className="w-5 sm:w-6 h-5 sm:h-6 text-primary" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">{benefit.title}</h3>
                        <p className="text-sm sm:text-base text-gray-600">{benefit.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonial */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 md:p-5">
              <div className="max-w-3xl">
                <div className="flex items-center gap-1.5 sm:gap-1.5 md:gap-1.5 mb-3 sm:mb-3 md:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-base sm:text-base md:text-lg">★</span>
                  ))}
                </div>
                <p className="text-base sm:text-base md:text-base text-gray-700 italic mb-3 sm:mb-3 md:mb-3 leading-relaxed">
                  "{lpData.testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-sm sm:text-sm md:text-sm text-gray-900">{lpData.testimonial.name}</p>
                  <p className="text-xs sm:text-xs md:text-xs text-gray-600">{lpData.testimonial.role}</p>
                </div>
              </div>
            </section>

            {/* Mobile Form - shown before FAQ on mobile */}
            <div className="lg:hidden">
              {formContent}
            </div>

            {/* Formations recommandées — maille interne vers les pages formation */}
            {recommendedCourses.length > 0 && (
              <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 md:p-5">
                <h2 className="flex items-center gap-2 text-xl sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">
                  <GraduationCap className="h-5 sm:h-6 w-5 sm:w-6 text-primary" />
                  Formations recommandées
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recommendedCourses.map((course) => (
                    <Link
                      key={course.id}
                      to={`/formationIA/${course.slug}/`}
                      className="group flex flex-col justify-between bg-gray-50 hover:bg-primary/5 rounded-xl p-4 transition-colors"
                    >
                      <div>
                        <span className="inline-block bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide py-1 px-3 rounded-full mb-3">
                          {course.duration}
                        </span>
                        <p className="font-semibold text-gray-900 mb-1 leading-snug">{course.title}</p>
                        <p className="text-sm text-gray-500 line-clamp-2">{course.description}</p>
                      </div>
                      <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                        Voir la formation
                        <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* FAQ Section */}
            <section>
              <h2 className="text-2xl sm:text-2xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 md:mb-8">Questions fréquentes</h2>
              <div className="space-y-3 sm:space-y-3 md:space-y-3">
                {lpData.faq.map((item, idx) => (
                  <div key={idx} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setExpandedFAQ(expandedFAQ === idx ? null : idx)}
                      className="w-full px-4 sm:px-4 md:px-4 py-3 sm:py-2 md:py-2 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <span className="font-semibold text-sm sm:text-sm md:text-sm text-gray-900 text-left">{item.q}</span>
                      <ChevronDown
                        className={`w-4 sm:w-4 md:w-4 h-4 sm:h-4 md:h-4 text-gray-600 transition-transform duration-300 flex-shrink-0 ${
                          expandedFAQ === idx ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFAQ === idx && (
                      <div className="px-4 sm:px-4 md:px-4 py-2 sm:py-2 md:py-2 bg-gray-50 border-t border-gray-100">
                        <p className="text-sm sm:text-sm md:text-sm text-gray-700">{item.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </motion.div>

          {/* Right Column: Form (Desktop only) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block lg:sticky lg:top-24"
          >
            {formContent}
          </motion.div>
        </div>
      </main>

      {/* Minimal Footer */}
      <footer className="bg-white border-t border-gray-100 py-4 sm:py-4 md:py-6 mt-8 sm:mt-8 md:mt-8">
        <div className="container-custom text-center">
          <p className="text-gray-600 text-xs sm:text-xs md:text-xs mb-2 sm:mb-2 md:mb-2">Upnov IA — Formations Intelligence Artificielle pour Entreprises</p>
          <p className="text-gray-500 text-xs mt-2 sm:mt-2 md:mt-2">&copy; {new Date().getFullYear()} Upnov IA. Tous droits réservés.</p>
        </div>
      </footer>

      {/* Exit Intent Popup */}
      <ExitIntentPopup
        isOpen={showExitPopup}
        onClose={handleExitPopupClose}
        description={lpData.exitText || "Comment pouvons-nous vous aider ?"}
        quickQuestions={lpData.exitFAQ}
      />
    </div>
  );
};

export default LandingPage;
